import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { envConfig } from '@/plugins/env'
import store from '@/store'
import { EnumHttpStatusCode } from '@/common/models/enum'
import { errorNotify } from '@/modules/notify'
import { i18n } from '@/plugins/i18n'
import router from '@/router'
import { hideUiBlocker, resetUiBlockCounter, showUiBlocker } from '@/modules/apiExtensions'


let isRefreshTokenInProgress = false

const api = axios.create()

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {

    const routeName: any = router.currentRoute?.value?.name
    showUiBlocker(routeName, config)

    config.baseURL = `${envConfig.VUE_APP_HOST_BACKEND_URL}api/crm`
    // Extended timeout due to https://cybersoft4u.atlassian.net/browse/CYBSS-1250
    config.timeout = 60_000
    config.headers['Content-Type'] = 'application/json'

    if (store.getters['user/tokens'].accessToken) {
      config.headers.Authorization = `Bearer ${store.getters['user/tokens'].accessToken}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

api.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    hideUiBlocker(response)
    return response
  },
  async (error: any) => {

    resetUiBlockCounter()

    const httpErr: any = error
    const _t: any = i18n.global

    // Start Handling issues
    if (error.code === 'ECONNABORTED') {
      // Timeout error
      errorNotify(_t.tc('common.error.httpTimeoutError'))
      // httpErr = new HttpTimeoutError(error, error.config.url)
      // JL.fatal(httpErr.toServerString())
      return Promise.reject(httpErr) // Return original response
    }

    const { config: originalRequest, response } = error
    // const url = originalRequest ? originalRequest.url : ''

    if (!error.response) {
      errorNotify(_t.tc('common.error.httpNoStatusError'))
      // httpErr = new HttpNoStatusError(error, url)
      // JL.error(httpErr.toServerString())
      return Promise.reject(httpErr)
    }

    let isRefreshOk: boolean = false
    let status: any


    if (!response) {
      errorNotify(_t.tc('common.error.httpNoStatusError'))
      // httpErr = new HttpNoStatusError(error, url)
      // JL.error(httpErr.toServerString())
      return Promise.reject(httpErr)
    } else {
      status = response.status
    }

    if (status === EnumHttpStatusCode.INVALID_TOKEN) {
      if (!isRefreshTokenInProgress) {
        isRefreshTokenInProgress = true
        isRefreshOk = await store.dispatch('user/refreshToken')
        isRefreshTokenInProgress = false
      }

      if (isRefreshOk) {
        const retryOriginalRequest = new Promise(async (resolve) => {
          const res = await api(originalRequest)
          // DANGER: here will clean store
          location.reload()
          resolve(res)
        })

        return retryOriginalRequest // Return the response from original request with new token

      } else {
        // normally 489 should not show any toast, refresh token 498 will handle in its api layer

        // httpErr = new HttpInvalidTokenError(error, url)
        // JL.error(httpErr.toServerString())
        return Promise.reject(httpErr) // Return original response
      }
    }
    else if (status === EnumHttpStatusCode.TOKEN_ERROR) {
      await store.dispatch('user/logout')
    } else {
      /* TODO: here is vue 2 to vue 3 migrate tech issue, api interceptor can't get vue instance, should refactor
        // Non 498 response
        console.log('Non 498 response')
        switch (status) {
          case EnumHttpStatusCode.UNAUTHORIZED:
            httpErr = new HttpUnauthorizedError(error, url)
            // JL.warn(httpErr.toServerString())
            break
          default:
            httpErr = new HttpRequestError(error, url, status, response.data)
            // JL.fatal(httpErr.toServerString())
            break
        }*/
      if (status >= EnumHttpStatusCode.INTERNAL_SERVER_ERROR) {
        errorNotify(_t.tc('common.error.generic'))
      }

      return Promise.reject(httpErr)
    }

    return httpErr
  },
)

export default api
